export class Visualizer {
    constructor(canvas, analyser) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d', { alpha: false }); // Optimize for no transparency on main canvas
        this.analyser = analyser;
        
        // State
        this.heatPoints = [];
        this.waveformHistory = [];
        this.historySize = 15;
        this.theme = null;
        this.colorMap = new Uint8ClampedArray(256 * 4); // RGBA lookup table
        
        // Spectrogram
        this.showSpectrogram = true;
        this.specCanvas = document.createElement('canvas');
        this.specCtx = this.specCanvas.getContext('2d', { alpha: false });
        this.specCanvas.width = window.innerWidth;
        this.specCanvas.height = window.innerHeight;
        
        // Buffers
        this.bufferLength = 0;
        this.timeData = null;
        this.freqData = null;
        
        // Bindings
        this.resize();
        window.addEventListener('resize', () => this.resize());
    }

    resize() {
        this.canvas.width = window.innerWidth;
        this.canvas.height = window.innerHeight;
        this.specCanvas.width = window.innerWidth;
        this.specCanvas.height = window.innerHeight;
        
        // Re-init buffers if needed? 
        // Analyser buffer size is constant usually, but arrays might need update if we change fftSize dynamically.
    }

    setTheme(theme) {
        this.theme = theme;
        this.createColorMap();
    }

    createColorMap() {
        if (!this.theme || !this.theme.specColors) return;
        
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 1;
        const ctx = canvas.getContext('2d');
        
        const grad = ctx.createLinearGradient(0, 0, 256, 0);
        const colors = this.theme.specColors;
        colors.forEach((color, i) => {
            grad.addColorStop(i / (colors.length - 1), color);
        });
        
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, 256, 1);
        
        this.colorMap = ctx.getImageData(0, 0, 256, 1).data;
    }

    addHeatPoint(x, y) {
        this.heatPoints.push({
            x: x,
            y: y,
            life: 1.0,
            age: 0
        });
    }

    updateBuffers() {
        if (!this.analyser) return;
        
        this.bufferLength = this.analyser.frequencyBinCount;
        if (!this.timeData || this.timeData.length !== this.bufferLength) {
            this.timeData = new Uint8Array(this.bufferLength);
            this.freqData = new Uint8Array(this.bufferLength);
        }
        
        this.analyser.getByteTimeDomainData(this.timeData);
        this.analyser.getByteFrequencyData(this.freqData);
        
        // History for 3D scope
        this.waveformHistory.unshift(new Uint8Array(this.timeData));
        if (this.waveformHistory.length > this.historySize) {
            this.waveformHistory.pop();
        }
    }

    draw() {
        if (!this.analyser || !this.theme) return;
        
        this.updateBuffers();
        const { width, height } = this.canvas;

        // --- 1. Background / Spectrogram ---
        if (this.showSpectrogram) {
            this.drawSpectrogram(width, height);
            this.ctx.drawImage(this.specCanvas, 0, 0);
            
            // Add a semi-transparent overlay to fade it slightly for foreground contrast
            this.ctx.fillStyle = this.theme.bgFade; 
            this.ctx.fillRect(0, 0, width, height);
        } else {
            this.ctx.fillStyle = this.theme.bgFade;
            this.ctx.fillRect(0, 0, width, height);
        }

        // --- 2. Heat Map ---
        this.drawHeatMap(width, height);

        // --- 3. Spectrum Bars ---
        this.drawSpectrum(width, height);

        // --- 4. Oscilloscope ---
        this.drawScope(width, height);
    }

    drawSpectrogram(width, height) {
        // Shift existing image down by 1 pixel
        // Note: multiple drawImage calls can be expensive. 
        // Optimization: Scroll only every N frames or use circular buffer logic?
        // For simple waterfall, drawImage is usually fast enough on desktops.
        
        this.specCtx.drawImage(this.specCanvas, 0, 0, width, height - 1, 0, 1, width, height - 1);
        
        // Draw new line at top (y=0)
        // We map frequency bins to X pixels
        const barWidth = width / this.bufferLength; 
        
        // Optimize: Create an ImageData line? Or fillRects?
        // fillRects is easier to prototype. ImageData is faster.
        // Let's use ImageData for the single row.
        const rowData = this.specCtx.createImageData(width, 1);
        const data = rowData.data; // RGBA
        
        // Map bins to pixels
        // We want log scale ideally, but linear is standard for this analzyer setup
        for (let x = 0; x < width; x++) {
            const binIndex = Math.floor((x / width) * this.bufferLength);
            const value = this.freqData[binIndex];
            
            // Fast lookup from colorMap
            const mapIdx = value * 4;
            const idx = x * 4;
            
            data[idx] = this.colorMap[mapIdx];
            data[idx+1] = this.colorMap[mapIdx+1];
            data[idx+2] = this.colorMap[mapIdx+2];
            data[idx+3] = 255;
        }
        
        this.specCtx.putImageData(rowData, 0, 0);
    }

    drawHeatMap(width, height) {
        if (this.heatPoints.length === 0) return;
        
        this.ctx.save();
        this.ctx.globalCompositeOperation = 'lighter';
        
        for (let i = this.heatPoints.length - 1; i >= 0; i--) {
            const p = this.heatPoints[i];
            const px = p.x * width;
            const py = p.y * height;
            
            p.life -= 0.02;
            p.age++;
            if (p.life <= 0) {
                this.heatPoints.splice(i, 1);
                continue;
            }
            
            const radius = 20 + (1.0 - p.life) * 30;
            const gradient = this.ctx.createRadialGradient(px, py, 0, px, py, radius);
            
            // Apply Theme Gradient
            const g1 = this.theme.trailGradient[0].replace(/[\d.]+\)$/, `${p.life * 0.5})`);
            const g2 = this.theme.trailGradient[1].replace(/[\d.]+\)$/, `${p.life * 0.2})`);
            
            gradient.addColorStop(0, g1); 
            gradient.addColorStop(0.5, g2); 
            gradient.addColorStop(1, 'rgba(0,0,0,0)');
            
            this.ctx.fillStyle = gradient;
            this.ctx.beginPath();
            this.ctx.arc(px, py, radius, 0, Math.PI * 2);
            this.ctx.fill();
        }
        this.ctx.restore();
    }

    drawSpectrum(width, height) {
        const barWidth = (width / this.bufferLength) * 5;
        const centerY = height / 2;
        const usefulBins = this.bufferLength / 4; 
        
        for (let i = 0; i < usefulBins; i++) {
            const v = this.freqData[i]; 
            const barHeight = (v / 255) * (height / 2.5);
            
            const t = i / usefulBins;
            const hue = this.theme.spectrumHueStart + t * (this.theme.spectrumHueEnd - this.theme.spectrumHueStart);
            const lig = 50 + (v / 255) * 20;
            
            this.ctx.fillStyle = `hsl(${hue}, 100%, ${lig}%)`;
            this.ctx.fillRect(width / 2 + (i * barWidth), centerY - barHeight / 2, barWidth - 1, barHeight);
            this.ctx.fillRect(width / 2 - (i * barWidth) - barWidth, centerY - barHeight / 2, barWidth - 1, barHeight);
        }
    }

    drawScope(width, height) {
        // RMS for pulse
        let sum = 0;
        for (let i = 0; i < this.bufferLength; i++) {
            const v = (this.timeData[i] - 128) / 128;
            sum += v * v;
        }
        const amplitude = Math.sqrt(sum / this.bufferLength);

        // Ghosts
        this.ctx.save();
        this.ctx.translate(width / 2, height / 2);
        for (let h = this.waveformHistory.length - 1; h >= 0; h--) {
            const historyData = this.waveformHistory[h];
            const depth = h / this.historySize;
            const scale = 1.0 - (depth * 0.3);
            const alpha = (1.0 - depth) * 0.3;
            
            if (alpha <= 0.01) continue;
            
            this.ctx.lineWidth = 1;
            this.ctx.strokeStyle = `rgba(255, 255, 255, ${alpha})`;
            this.ctx.shadowBlur = 0;
            
            this.ctx.save();
            this.ctx.scale(scale, scale);
            this.ctx.beginPath();
            
            const sliceW = width / historyData.length;
            for (let i = 0; i < historyData.length; i++) {
                const v = historyData[i] / 128.0;
                const y = (v - 1) * (height / 2);
                const x = (i * sliceW) - (width / 2);
                if (i === 0) this.ctx.moveTo(x, y);
                else this.ctx.lineTo(x, y);
            }
            this.ctx.stroke();
            this.ctx.restore();
        }
        this.ctx.restore();

        // Main Waveform
        this.ctx.lineWidth = 3 + (amplitude * 15);
        this.ctx.strokeStyle = '#fff';
        this.ctx.shadowBlur = 10 + (amplitude * 40);
        this.ctx.shadowColor = this.theme.scopeColor;
        this.ctx.beginPath();
        
        const sliceWidth = width * 1.0 / this.bufferLength;
        let x = 0;
        for (let i = 0; i < this.bufferLength; i++) {
            const v = this.timeData[i] / 128.0;
            const y = v * height / 2;
            if (i === 0) this.ctx.moveTo(x, y);
            else this.ctx.lineTo(x, y);
            x += sliceWidth;
        }
        this.ctx.lineTo(width, height / 2);
        this.ctx.stroke();
        this.ctx.shadowBlur = 0;
    }
}
